/**
 * Type definitions for Task Management
 * TASK-355: Task & Follow-up Management System (UC-04, UC-05, UC-06)
 * TASK-358: Frontend AddTaskModal Component
 *
 * These types match the backend Pydantic models in:
 * backend/agents/data-ingestion/routes/task_routes.py
 */

// ============= Task Types =============

/**
 * Task types - unifies follow-ups and general tasks
 */
export type TaskType =
  // Follow-up types
  | 'follow_up_call'
  | 'follow_up_email'
  | 'follow_up_meeting'
  | 'follow_up_document'
  | 'follow_up_other'
  // General task types
  | 'task_general'
  | 'task_review'
  | 'task_compliance'
  | 'task_research'
  | 'task_admin'
  // TASK-576: mandatory-compliance life-event actions (distinct audit labels)
  | 'sow_review'
  | 'crs_recert'
  | 'suitability_reassessment';

/**
 * Task priority levels
 */
export type TaskPriority = 'low' | 'medium' | 'high' | 'critical';

/**
 * Task status values
 */
// `review` (TASK-684) = the AI finished work that needs human sign-off. The backend can
// emit it as of migration 197, so the union must admit it or the type is a lie.
// `changes_requested` (TASK-744, migration 218) = a reviewer sent the work back with
// structured feedback — the return path of the review state machine.
export type TaskStatus =
  | 'pending' | 'in_progress' | 'review' | 'changes_requested' | 'completed' | 'cancelled';

// ============= Task Interfaces =============

/**
 * Task reminder configuration
 */
export interface TaskReminder {
  scheduled_at: string; // ISO datetime
}

/**
 * Task response from API
 */
/**
 * TASK-922 — what happened, for work raised from a signal.
 *
 * MUST stay identical to `TASK_OUTCOMES` in
 * `backend/agents/data-ingestion/routes/task_routes.py`, which rejects anything else with a
 * 400. Pinned by `test_task_outcomes_parity.py`, for the reason TASK-820 records: the Alerts
 * page grew its own copy of the admission catalogue and three sources rendered as raw slugs.
 * A hand-kept second list drifts; a hand-kept second list plus a test does not.
 *
 * The ORDER here is the order the picker offers, and it is not alphabetical — it runs from
 * "I fixed it" to "I looked and chose not to", because that is the order an advisor thinks in.
 */
export const TASK_OUTCOMES = [
  'implemented_change',
  'accepted_exception',
  'deferred',
  'investigation_complete_no_action',
] as const;

export type TaskOutcome = (typeof TASK_OUTCOMES)[number];

/**
 * What each outcome MEANS for the signal, in the advisor's words.
 *
 * `effect` mirrors `_OUTCOME_EFFECT` server-side and is shown, not hidden: the whole point of
 * TASK-724 is that ticking done does not retire a condition, and an advisor who cannot see
 * that will keep expecting it to. None of these closes a subject — closure comes from a
 * successful re-evaluation or a valid acceptance.
 */
export const TASK_OUTCOME_COPY: Record<
  TaskOutcome,
  { label: string; effect: string }
> = {
  implemented_change: {
    label: 'I made the change',
    effect: 'Stays open until the next Radar sweep confirms it.',
  },
  accepted_exception: {
    label: 'Accepted as an exception',
    effect: 'Suppressed while that acceptance is valid.',
  },
  deferred: {
    label: 'Not now — come back to it',
    effect: 'Returns on the date you pick, if it is still true.',
  },
  investigation_complete_no_action: {
    label: 'Looked at it, no action needed',
    effect: 'The signal stays as it is; only policy may dismiss it.',
  },
};

export interface Task {
  id: string;
  title: string;
  description?: string;
  task_type?: TaskType;
  priority: TaskPriority;
  status: TaskStatus;
  due_date?: string;
  completed_at?: string;
  snoozed_until?: string;
  client_id?: string;
  assignee_user_id?: string;
  assigned_by_user_id?: string;
  note_id?: string;
  related_entity_type?: string;
  related_entity_id?: string;
  /**
   * TASK-724 / TASK-922 — the durable binding to the SIGNAL SUBJECT this work is about.
   * `related_entity_id` used to hold a `change_events` row id, and that projection is
   * destroyed and rebuilt every 15 minutes, so the reference dangled almost immediately.
   *
   * The API has returned these on every task since TASK-724 (`task_models.to_dict`) and the
   * client dropped all of them — which is why completing a signal-bound task from Today
   * answered 400 `outcome_required`: nothing on this side could see that the task WAS
   * signal-bound. Present means "this completion has to say what happened".
   */
  subject_key?: string | null;
  subject_source_event?: string | null;
  outcome?: TaskOutcome | null;
  outcome_rationale?: string | null;
  outcome_at?: string | null;
  outcome_by?: string | null;
  source?: string; // TASK-593/576: origin of the task (manual, alert, drift, life_event, …)
  // TASK-683: provenance the API has always returned (task_models.to_dict) and the client
  // has always dropped. Absent on manually created tasks — absent means absent, never a
  // placeholder attribution.
  transcript_excerpt?: string | null;
  transcript_line_number?: number | null;
  confidence_score?: number | null;
  /** TASK-683 review: which meeting the excerpt came from, so the quote can be opened. */
  session_id?: string | null;
  /**
   * TASK-702 — why an agent parked this task, derived from the LAST `attempted` event.
   * Present only while that attempt was blocked; a later prepared attempt clears it.
   * `work_task` leaves a blocked outcome as `in_progress`, so the board needs this to tell
   * a blocked row apart from an idle one.
   */
  blocked_by?: string | null;
  prerequisites?: string[];
  calendar_event_id?: string;
  reminder_datetime?: string;
  reminder_sent: boolean;
  version: number;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Request payload for creating a new task
 */
export interface TaskCreateRequest {
  title: string;
  description?: string;
  task_type: TaskType;
  due_date?: string;
  priority?: TaskPriority;
  client_id?: string;
  assignee_user_id?: string;
  note_id?: string;
  related_entity_type?: string;
  related_entity_id?: string;
  reminder?: TaskReminder;
  source?: string; // TASK-593: origin of the task (manual, alert, drift, life_event, …)
}

/**
 * Request payload for updating a task
 */
export interface TaskUpdateRequest {
  title?: string;
  description?: string;
  task_type?: TaskType;
  due_date?: string;
  priority?: TaskPriority;
  status?: TaskStatus;
  client_id?: string;
  assignee_user_id?: string;
  reminder_datetime?: string;
  version: number; // Required for optimistic locking
}

/**
 * Request payload for snoozing a task
 */
export interface TaskSnoozeRequest {
  snooze_until: string; // ISO datetime
}

// ============= API Response Types =============

/**
 * Response for single task operations
 */
/**
 * TASK-702 — what the agent actually did, and what it produced.
 *
 * These live on the DETAIL response only. They are deliberately NOT on the list-row `Task`:
 * a review queue renders dozens of rows and none of them need an event history until the RM
 * opens one.
 */
export interface TaskEvent {
  id: string;
  task_id: string;
  at: string | null;
  /** User id for a human, specialist name for an agent. */
  actor: string;
  actor_kind: 'human' | 'agent';
  /** created | status_changed | claimed | attempted | artifact_added | signed_off | dismissed */
  event: string;
  from_status?: string | null;
  to_status?: string | null;
  detail?: {
    approach?: string;
    outcome?: 'prepared' | 'blocked' | string;
    summary?: string;
    blocked_by?: string;
    artifact_type?: string;
    [k: string]: unknown;
  } | null;
  source_excerpt?: string | null;
  source_locator?: Record<string, unknown> | null;
}

/** A POINTER to something the AI produced — never the content itself. */
/**
 * Server-side resolution of an artifact pointer (TASK-702).
 *
 * `task_artifacts` stores a type and an id in some other store. The server probes that store
 * before the client offers an "Open" control, because a pointer is not a destination: both
 * live artifacts on the demo account point at nothing (one id is not even a uuid), and used to
 * render as working links onto the app's not-found page.
 */
export interface ArtifactResolution {
  /** available: the row exists · missing: it does not · unsupported: no opener for the type. */
  state: 'available' | 'missing' | 'unsupported';
  /** An in-app path, only ever set when `state === 'available'`. */
  route: string | null;
  reason: string | null;
}

export interface TaskArtifact {
  id: string;
  task_id: string;
  artifact_type: string;
  artifact_id: string;
  artifact_version?: number | null;
  created_at?: string | null;
  /** Absent on an older server; the client then falls back to its own type mapping. */
  resolution?: ArtifactResolution | null;
}

/**
 * TASK-743 — a binding is durable attachment POLICY for one `(task, artifact_type,
 * artifact_id)` identity: role, requirement, classification, withdrawal. The version-sensitive
 * `TaskArtifact` rows are its evidence children.
 */
export type TaskArtifactRole = 'output' | 'supporting' | 'source' | 'unclassified';

export interface TaskArtifactBinding {
  id: string;
  task_id: string;
  artifact_type: string;
  artifact_id: string;
  role: TaskArtifactRole;
  required_for_completion: boolean;
  is_active: boolean;
  created_at?: string | null;
  created_by?: string | null;
  created_by_kind?: string | null;
  classified_at?: string | null;
  withdrawn_at?: string | null;
  withdrawn_by?: string | null;
  withdrawal_reason?: string | null;
}

/** `POST /api/tasks/{id}/artifacts` — attach evidence, upserting the binding first. */
export interface AttachTaskArtifactRequest {
  artifact_type: string;
  artifact_id: string;
  artifact_version?: number;
  role?: TaskArtifactRole;
  /** Only valid for `artifact_type === 'document'` (DB CHECK). */
  required_for_completion?: boolean;
}

/** `PATCH /api/tasks/{id}/bindings`. `withdraw` REQUIRES `reason` (422 otherwise). */
export interface TaskBindingMutationRequest {
  artifact_type: string;
  artifact_id: string;
  action: 'classify' | 'withdraw' | 'reactivate';
  role?: TaskArtifactRole;
  required_for_completion?: boolean;
  reason?: string;
}

export interface TaskBindingResponse {
  success: boolean;
  binding: TaskArtifactBinding;
}

/**
 * `GET /api/tasks/{id}`. `trail_complete` / `trail_gaps` are DERIVED server-side from the
 * event sequence (TASK-702) — they are not stored anywhere, and must not be cached as if
 * they were.
 */
export interface TaskDetailResponse {
  success: boolean;
  task: Task;
  events: TaskEvent[];
  artifacts: TaskArtifact[];
  /** TASK-743: attachment policy rows; evidence in `artifacts` groups under these. */
  bindings: TaskArtifactBinding[];
  trail_complete: boolean;
  trail_gaps: string[];
  /** Set only while the last attempt was blocked; cleared once a later attempt prepares. */
  blocked_by?: string | null;
}

export interface TaskResponse {
  success: boolean;
  task: Task;
  message?: string;
}

/**
 * Response for task list
 */
export interface TaskListResponse {
  success: boolean;
  tasks: Task[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

/**
 * Dashboard statistics
 */
export interface TaskDashboardStats {
  total_tasks: number;
  pending_tasks: number;
  overdue_tasks: number;
  completed_today: number;
  high_priority_pending: number;
  tasks_due_this_week: number;
}

/**
 * Response for dashboard stats
 */
export interface TaskDashboardStatsResponse {
  success: boolean;
  stats: TaskDashboardStats;
}

// ============= Filter Types =============

/**
 * Task list filter parameters
 */
export interface TaskListFilters {
  status?: TaskStatus | 'overdue';
  task_type?: TaskType;
  client_id?: string;
  assignee_user_id?: string;
  priority?: TaskPriority;
  due_before?: string;
  due_after?: string;
  include_snoozed?: boolean;
  limit?: number;
  offset?: number;
  sort_by?: 'due_date' | 'priority' | 'created_at' | 'updated_at';
  sort_order?: 'asc' | 'desc';
}

// ============= UI Helper Types =============

/**
 * Task type option for UI selection
 */
export interface TaskTypeOption {
  value: TaskType;
  label: string;
  category: 'follow_up' | 'task';
  icon?: string;
}

/**
 * All task type options for UI
 */
export const TASK_TYPE_OPTIONS: TaskTypeOption[] = [
  // Follow-up types
  { value: 'follow_up_call', label: 'Follow-up Call', category: 'follow_up' },
  { value: 'follow_up_email', label: 'Follow-up Email', category: 'follow_up' },
  { value: 'follow_up_meeting', label: 'Follow-up Meeting', category: 'follow_up' },
  { value: 'follow_up_document', label: 'Follow-up Document', category: 'follow_up' },
  { value: 'follow_up_other', label: 'Other Follow-up', category: 'follow_up' },
  // General task types
  { value: 'task_general', label: 'General Task', category: 'task' },
  { value: 'task_review', label: 'Review Task', category: 'task' },
  { value: 'task_compliance', label: 'Compliance Task', category: 'task' },
  { value: 'task_research', label: 'Research Task', category: 'task' },
  { value: 'task_admin', label: 'Administrative', category: 'task' },
  // TASK-576: mandatory-compliance life-event actions
  { value: 'sow_review', label: 'Source-of-Wealth Review', category: 'task' },
  { value: 'crs_recert', label: 'CRS/FATCA Re-certification', category: 'task' },
  { value: 'suitability_reassessment', label: 'Suitability Reassessment', category: 'task' },
];

/**
 * Priority options for UI
 */
export const PRIORITY_OPTIONS: { value: TaskPriority; label: string; color: string }[] = [
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-700' },
  { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-700' },
  { value: 'high', label: 'High', color: 'bg-amber-100 text-amber-700' },
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-700' },
];

/**
 * Status options for UI
 */
export const STATUS_OPTIONS: { value: TaskStatus; label: string; color: string }[] = [
  { value: 'pending', label: 'Pending', color: 'bg-gray-100 text-gray-700' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-blue-100 text-blue-700' },
  // Amber, not blue: "waiting on you" has to read differently from "being worked on".
  { value: 'review', label: 'Needs Review', color: 'bg-amber-100 text-amber-700' },
  // TASK-744: the reviewer sent it back — the producer owes a revision.
  { value: 'changes_requested', label: 'Changes Requested', color: 'bg-orange-100 text-orange-700' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-700' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700' },
];

/**
 * Helper to get task type label
 */
export function getTaskTypeLabel(taskType: TaskType): string {
  const option = TASK_TYPE_OPTIONS.find((o) => o.value === taskType);
  return option?.label || taskType;
}

/**
 * Helper to check if a task is a follow-up
 */
export function isFollowUp(taskType: TaskType): boolean {
  return taskType.startsWith('follow_up_');
}

/**
 * Helper to get priority badge class
 */
export function getPriorityClass(priority: TaskPriority): string {
  const option = PRIORITY_OPTIONS.find((o) => o.value === priority);
  return option?.color || '';
}

/**
 * Helper to get status badge class
 */
export function getStatusClass(status: TaskStatus): string {
  const option = STATUS_OPTIONS.find((o) => o.value === status);
  return option?.color || '';
}


/** TASK-758: an artifact pointer that does not resolve, for the repair queue. */
export interface UnresolvableArtifact {
  id: string;
  task_id: string;
  task_title?: string | null;
  task_status?: string | null;
  artifact_type: string;
  artifact_id: string;
  /** Verification outcome: `missing` | `invalid` | `unknown` — never `verified`. */
  state: string;
  /** Server-chosen code, e.g. `no_such_record` | `identifier_shape`. */
  reason_code?: string | null;
  /** Always the resolver's own words. The widget renders it and never invents one. */
  reason?: string | null;
}

export interface UnresolvableArtifactsResponse {
  success: boolean;
  artifacts: UnresolvableArtifact[];
  total: number;
}
